import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import * as authApi from '../api/auth'
import { setAuthInitialized } from '../api/client'
import type { ProfileResponse } from '../types'

interface AuthContextType {
  user: ProfileResponse | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  loginGoogle: (code: string) => Promise<void>
  logout: () => void
  refreshProfile: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | null>(null)

function setWindowToken(access: string) {
  ;(window as Window & { __fasico_token?: string }).__fasico_token = access
  sessionStorage.setItem('fasico_token', access)
}

function clearWindowTokens() {
  ;(window as Window & { __fasico_token?: string }).__fasico_token = undefined
  sessionStorage.removeItem('fasico_token')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ProfileResponse | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)

  const logout = useCallback(() => {
    authApi.logout().catch(() => undefined)
    clearWindowTokens()
    setToken(null)
    setUser(null)
  }, [])

  // Silent refresh on mount to recover session via httpOnly cookie
  useEffect(() => {
    const silentRefresh = async () => {
      try {
        const tokens = await authApi.refreshToken()
        setWindowToken(tokens.access_token)
        setToken(tokens.access_token)
        setAuthInitialized()
        const profile = await authApi.getProfile()
        setUser(profile)
      } catch {
        sessionStorage.removeItem('fasico_token')
        clearWindowTokens()
      }
      setIsLoading(false)
      setIsInitialized(true)
      setAuthInitialized()
    }
    silentRefresh()
  }, [])

  // Listen for logout events dispatched by axios interceptor
  useEffect(() => {
    window.addEventListener('fasico:logout', logout)
    return () => window.removeEventListener('fasico:logout', logout)
  }, [logout])

  const handleTokens = async (access: string) => {
    setWindowToken(access)
    setToken(access)
    const profile = await authApi.getProfile()
    setUser(profile)
  }

  const login = async (email: string, password: string) => {
    const tokens = await authApi.login(email, password)
    await handleTokens(tokens.access_token)
  }

  const register = async (email: string, password: string) => {
    const tokens = await authApi.register(email, password)
    await handleTokens(tokens.access_token)
  }

  const loginGoogle = async (code: string) => {
    const tokens = await authApi.loginGoogle(code)
    await handleTokens(tokens.access_token)
  }

  const refreshProfile = async () => {
    const profile = await authApi.getProfile()
    setUser(profile)
  }

  if (!isInitialized) return null

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        login,
        register,
        loginGoogle,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
